import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { extractApiError } from '../../helpers/apiErrors';
import type { SendingFacility } from '../../helpers/sendingFacility';
import type { Customer } from '../../pages/cardex/api/useCustomer';
import type { EmailTemplate } from '../../pages/email-templates/api/useEmailTemplate';
import type { WhatsappTemplate } from '../../pages/settings/whatsapp/useWhatsappTemplate';

import { useEnvoyerMessage } from '../api/useComposeur';
import { t } from '../i18n/langue';
import { useCleIdempotence } from './useFenetreWhatsapp';
import type { CleCanal } from '../config/canaux';

interface UseEnvoiComposeurParams {
    client: Customer | undefined;
    /**
     * L'hôtel d'envoi ET sa fiche client (retour Odomo 2026-09) ; `null` tant que la
     * fiche consolidée a plusieurs hôtels et qu'aucun n'est choisi.
     */
    identite: SendingFacility | null;
    /**
     * `null` avant que la messagerie n'ait déterminé de canal joignable : dans ce
     * cas l'écran n'affiche ni barre ni feuille, `envoyer` n'est jamais invoqué.
     */
    canal: CleCanal | null;
    brouillon: string;
    /** Vide le brouillon du STOCKAGE, pas seulement de l'écran. */
    viderBrouillon: () => void;
    surEmail: boolean;
    objet: string | null;
    setObjet: Dispatch<SetStateAction<string | null>>;
    objetParDefaut: string;
    /** Le message part : le fil doit le montrer (ancrage, audit 2026-09-23). */
    onEnvoye?: () => void;
}

/**
 * L'envoi du composeur — extrait de #84 (#19, #70).
 *
 * **Synchrone** : un refus de Meta est un 400 immédiat, porté tel quel — c'est la
 * seule information exploitable, pas « une erreur est survenue ». Une 422 montre
 * son premier message de champ (« Ce modèle n'appartient pas à… »).
 *
 * La clé d'idempotence ne se renouvelle QU'APRÈS un succès : tous les réessais
 * d'un même message la partagent (#70).
 */
export function useEnvoiComposeur({
    client,
    identite,
    canal,
    brouillon,
    viderBrouillon,
    surEmail,
    objet,
    setObjet,
    objetParDefaut,
    onEnvoye,
}: UseEnvoiComposeurParams) {
    const [erreur, setErreur] = useState<string | null>(null);
    const { cle, renouveler } = useCleIdempotence();
    const envoi = useEnvoyerMessage(client?.id ?? 0);

    // Un autre hôtel d'envoi n'est plus le même envoi : garder la clé ferait rejouer
    // par le serveur le succès du premier hôtel (délai dépassé côté client).
    const hotelEnvoi = identite?.facility_id;
    useEffect(() => {
        renouveler();
    }, [hotelEnvoi]);

    const envoyer = (charge: Record<string, unknown>) => {
        if (!identite) {
            // Le brouillon reste : il partira dès l'hôtel choisi.
            setErreur(t('composants.envoi.choisirHotel'));
            return;
        }

        setErreur(null);
        envoi.mutate(
            {
                // La fiche DE l'hôtel d'envoi : le serveur exige qu'elles concordent.
                customer_id: identite.customer_id,
                facility_id: identite.facility_id,
                type: canal === 'live_chat' ? 'live_chat' : canal,
                idempotency_key: cle(),
                ...charge,
            } as never,
            {
                onSuccess: () => {
                    onEnvoye?.();
                    // Envoi réussi : le brouillon n'a plus de raison d'exister.
                    viderBrouillon();
                    setObjet(null);
                    renouveler();
                },
                onError: (e: unknown) => {
                    // Le refus de Meta arrive en 400 immédiat, en anglais. On le montre
                    // tel quel plutôt que de le traduire en « une erreur est survenue ».
                    setErreur(extractApiError(e, t('composants.envoi.echec')));
                },
            },
        );
    };

    const envoyerTexte = () => {
        const texte = brouillon.trim();
        if (!texte) return;
        if (surEmail) {
            envoyer({ subject: objet ?? objetParDefaut, body: texte });
            return;
        }
        envoyer({ message: texte });
    };

    const envoyerModele = (modele: WhatsappTemplate, reservationId?: number) => {
        envoyer({
            message: '',
            whatsapp_template_id: modele.id,
            locale: client?.language_locale ?? 'fr',
            ...(reservationId ? { reservation_id: reservationId } : {}),
        });
    };

    /**
     * L'envoi d'un modèle d'e-mail choisi dans la bibliothèque — mêmes champs que
     * le web (`ChatSendingMessage.tsx` : `subject`, `body`, `email_template_id`).
     * `body` part vide : c'est le TEMPLATE qui fournit le corps, `body` n'est que
     * le contenu libre injecté dedans (`{{mail_content}}`, côté serveur) — voir
     * `MessageController::sendEmail`. Pas de `reservation_id` explicite : sans
     * réservation choisie, le serveur retombe sur `currentOrNextOrLastReservation`
     * pour les variables du template, exactement comme un e-mail texte libre
     * envoyé d'ici aujourd'hui.
     */
    const envoyerModeleEmail = (modele: EmailTemplate, sujet: string) => {
        envoyer({
            subject: sujet,
            body: '',
            email_template_id: modele.id,
        });
    };

    return { erreur, envoyerTexte, envoyerModele, envoyerModeleEmail, envoiEnCours: envoi.isLoading };
}
